import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import LongSheet from '@components/common/ScreenAnimations/components/LongSheet';
import { AnimatedBottomSheetProps } from './types/types';
import { BottomSheetAnimationContext } from './AnimationContext';
import { selectCloseTargetId, setCloseTargetId } from '@reducers/v2/bottomSheet/bottomSheet.slice';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { BOTTOM_SHEET_ANIMATION_TIMING, CLOSE_ALL_BOTTOM_SHEETS } from '@v2/pages/common/constants/base.constants';

// TODO: After 10+ open/close cycles, zombie WebDetails routes accumulate in the nav stack (each with its own
// AnimatedBottomSheet sharing one global Redux close flag). ESC/X can stop working because zombie LongSheet instances
// intercept events. Current cleanup-on-unmount prevents UI freeze, but a proper fix (closingRef guard) is still needed.
const AnimatedBottomSheet: React.FC<AnimatedBottomSheetProps> = ({ children, containerStyle, onPresentedChange, handleScroll, bottomSheetId }) => {
  const [presented, setPresented] = useState(true);
  const [isAnimationSettled, setIsAnimationSettled] = useState(false);
  const dispatch = useAppDispatch();
  const closeTargetId = useSelector(selectCloseTargetId);
  const isFirstRender = useRef(true);

  const animationContextValue = useMemo(() => ({ isAnimationSettled }), [isAnimationSettled]);

  useEffect(() => {
    return () => {
      // Silk-hq Sheet may leave orphaned focus-trap/backdrop elements in the DOM
      // when the component unmounts mid-animation. Clean them up.
      document.querySelectorAll('[data-silk-portal]').forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, []);

  useEffect(() => {
    // On first render, skip CLOSE_ALL signal - this prevents new sheets from immediately
    // closing due to a stale close-all signal that hasn't been cleaned up yet
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (closeTargetId === CLOSE_ALL_BOTTOM_SHEETS) {
        return;
      }
    }

    if (closeTargetId && (closeTargetId === bottomSheetId || closeTargetId === CLOSE_ALL_BOTTOM_SHEETS)) {
      // Trigger the close animation
      setPresented(false);
      // Call onPresentedChange after animation to handle cleanup (screen removal from nav)
      if (onPresentedChange) {
        setTimeout(() => {
          onPresentedChange();
        }, BOTTOM_SHEET_ANIMATION_TIMING);
      }
      // Only reset closeTargetId for single-target close
      if (closeTargetId === bottomSheetId) {
        dispatch(setCloseTargetId(null));
      }
    }
  }, [closeTargetId, dispatch, onPresentedChange, bottomSheetId]);

  return (
    <LongSheet.Root
      presented={presented}
      onPresentedChange={(value) => {
        setPresented((prevState) => {
          if (prevState && !value && onPresentedChange) {
            setTimeout(onPresentedChange, BOTTOM_SHEET_ANIMATION_TIMING);
          }

          return value;
        });
      }}
      license="commercial"
    >
      <LongSheet.Portal>
        <LongSheet.View
          nativeEdgeSwipePrevention={true}
          onTravelStatusChange={(status) => {
            setIsAnimationSettled(status === 'idleInside');
          }}
          style={{
            backgroundColor: 'transparent',
            pointerEvents: presented ? 'auto' : 'none',
            ...containerStyle,
          }}
        >
          <LongSheet.Content style={{ height: '100%' }} handleScroll={handleScroll}>
            <BottomSheetAnimationContext.Provider value={animationContextValue}>{children}</BottomSheetAnimationContext.Provider>
          </LongSheet.Content>
        </LongSheet.View>
      </LongSheet.Portal>
    </LongSheet.Root>
  );
};

export default AnimatedBottomSheet;
